/**
 * A régua da tela «Auditoria» (set/2026): quem abriu e quem comparou arquivos
 * pelo LinkFlow.
 *
 * Fica fora do `.tsx` porque o Node não testa JSX: os rótulos, a data, o
 * período que vira `from`/`to` e os filtros que vão e voltam pela URL.
 */
import type { AuditLogEntry, AuditLogKind, AuditLogParams, AuditLogViewerKind } from "./api";

export const AUDIT_LOG_PAGE_SIZE = 50;

export interface AuditLogFilters {
  /** Nome, e-mail ou arquivo. */
  q: string;
  acao: AuditLogKind | "";
  tipo: AuditLogViewerKind | "";
  /** `AAAA-MM-DD` do `<input type="date">`; vazio é sem limite. */
  de: string;
  ate: string;
  /** 1-based. */
  pagina: number;
  /**
   * A empresa do link (21/10/2026): `null` é todas; `""`, os links que chegaram
   * sem empresa; senão, o id dela.
   */
  org: string | null;
}

export const FILTROS_PADRAO: AuditLogFilters = {
  q: "",
  acao: "",
  tipo: "",
  de: "",
  ate: "",
  pagina: 1,
  org: null,
};

export function rotuloDaAcao(kind: string): string {
  if (kind === "viewer") return "Visualizou";
  if (kind === "compare") return "Comparou";
  return kind;
}

export function rotuloDoTipo(viewerKind: string): string {
  if (viewerKind === "document") return "Documento";
  if (viewerKind === "packaging") return "Embalagem";
  return viewerKind;
}

function doisDigitos(valor: number): string {
  return String(valor).padStart(2, "0");
}

/** `17/09/2026 14:02` no fuso do navegador; texto que não é data volta como veio. */
export function formatarDataHora(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return (
    `${doisDigitos(data.getDate())}/${doisDigitos(data.getMonth() + 1)}/${data.getFullYear()} ` +
    `${doisDigitos(data.getHours())}:${doisDigitos(data.getMinutes())}`
  );
}

const DIA = /^(\d{4})-(\d{2})-(\d{2})$/;

/** O dia do `<input type="date">`, ou `null` se não for um dia que existe. */
function diaValido(valor: string): [number, number, number] | null {
  const partes = DIA.exec(valor);
  if (!partes) return null;
  const [ano, mes, dia] = [Number(partes[1]), Number(partes[2]), Number(partes[3])];
  const data = new Date(ano, mes - 1, dia);
  // `new Date` aceita 31/02 e empurra para março: dia que não existe não filtra.
  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
    return null;
  }
  return [ano, mes, dia];
}

/**
 * O período da tela em instantes para a API: do começo do dia `de` ao fim do dia
 * `ate`, **no fuso do navegador** — «hoje» é o dia de quem está olhando, não o
 * dia em UTC.
 */
export function periodoDaConsulta(de: string, ate: string): { from?: string; to?: string } {
  const periodo: { from?: string; to?: string } = {};
  const inicio = diaValido(de);
  if (inicio) {
    periodo.from = new Date(inicio[0], inicio[1] - 1, inicio[2], 0, 0, 0, 0).toISOString();
  }
  const fim = diaValido(ate);
  if (fim) {
    periodo.to = new Date(fim[0], fim[1] - 1, fim[2], 23, 59, 59, 999).toISOString();
  }
  return periodo;
}

/** Os filtros da URL; valor desconhecido cai no padrão. */
export function filtrosDaUrl(params: URLSearchParams): AuditLogFilters {
  const acao = params.get("acao");
  const tipo = params.get("tipo");
  const de = params.get("de") ?? "";
  const ate = params.get("ate") ?? "";
  const pagina = Number.parseInt(params.get("pagina") ?? "", 10);
  return {
    q: (params.get("q") ?? "").trim(),
    acao: acao === "viewer" || acao === "compare" ? acao : "",
    tipo: tipo === "document" || tipo === "packaging" ? tipo : "",
    de: diaValido(de) ? de : "",
    ate: diaValido(ate) ? ate : "",
    pagina: Number.isInteger(pagina) && pagina >= 1 ? pagina : 1,
    // `org=` vazio é «sem empresa»; ausente é «todas».
    org: params.has("org") ? (params.get("org") ?? "").trim() : null,
  };
}

/** A URL dos filtros, só com o que difere do padrão. */
export function urlDosFiltros(filtros: AuditLogFilters): URLSearchParams {
  const params = new URLSearchParams();
  const q = filtros.q.trim();
  if (q) params.set("q", q);
  if (filtros.acao) params.set("acao", filtros.acao);
  if (filtros.tipo) params.set("tipo", filtros.tipo);
  if (filtros.de) params.set("de", filtros.de);
  if (filtros.ate) params.set("ate", filtros.ate);
  if (filtros.pagina > 1) params.set("pagina", String(filtros.pagina));
  if (filtros.org !== null && filtros.org !== undefined) params.set("org", filtros.org);
  return params;
}

/** Os filtros da tela como parâmetros de `GET /api/admin/audit-log`. */
export function consultaDosFiltros(filtros: AuditLogFilters): AuditLogParams {
  const consulta: AuditLogParams = {
    ...periodoDaConsulta(filtros.de, filtros.ate),
    offset: (Math.max(filtros.pagina, 1) - 1) * AUDIT_LOG_PAGE_SIZE,
    limit: AUDIT_LOG_PAGE_SIZE,
  };
  const q = filtros.q.trim();
  if (q) consulta.q = q;
  if (filtros.acao) consulta.kind = filtros.acao;
  if (filtros.tipo) consulta.viewer_kind = filtros.tipo;
  if (filtros.org !== null && filtros.org !== undefined) consulta.organization_id = filtros.org;
  return consulta;
}

/**
 * Onde reabrir o que a pessoa viu (out/2026), no viewer autenticado — ou `null`
 * quando não há o que abrir (o link já saiu do banco, ou falta um lado do par).
 *
 * As rotas são as do Gerenciador: arte em `/v/`, par de embalagens em
 * `/compare/A/B` e par de documentos no comparador semântico, com o **B** na
 * tela e o A no `?diff=` — a mesma ordem do link público de par, em que o B é o
 * «depois». O par documento × arte não aparece aqui: a emissão o recusa.
 */
export function linkDoQueFoiVisto(
  item: Pick<AuditLogEntry, "kind" | "viewer_kind" | "file_slug" | "file_b_slug">,
): string | null {
  const a = item.file_slug;
  if (!a) return null;
  if (item.kind !== "compare") return `/v/${encodeURIComponent(a)}`;
  const b = item.file_b_slug;
  if (!b) return null;
  return item.viewer_kind === "document"
    ? `/v/${encodeURIComponent(b)}?diff=${encodeURIComponent(a)}`
    : `/compare/${encodeURIComponent(a)}/${encodeURIComponent(b)}`;
}
